import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import CategoryFilters, { type RangeKey } from "./components/CategoryFilters";
import { filterArticles, getCategoryById, type SortKey } from "@/lib/articles";
import { applyCategorySeo } from "@/lib/seo";

const PAGE_SIZE = 6;

function rangeToDateFrom(range: RangeKey): string | undefined {
  if (range === "all") return undefined;
  const days = range === "week" ? 7 : range === "month" ? 30 : 90;
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);

  const [activeSub, setActiveSub] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [range, setRange] = useState<RangeKey>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setActiveSub("all");
    setSort("latest");
    setRange("all");
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [activeSub, sort, range]);

  useEffect(() => {
    if (category) {
      applyCategorySeo(
        category.name,
        `${category.name}频道为你精选${category.name}领域的最新资讯与深度内容：${category.description}`,
        `鲁啊鲁${category.name},${category.name}资讯,${category.subcategories
          .map((sub) => sub.name)
          .join(",")}`
      );
    }
  }, [category]);

  const results = useMemo(() => {
    if (!category) return [];
    return filterArticles({
      categoryId: category.id,
      subcategory: activeSub,
      sort,
      dateFrom: rangeToDateFrom(range),
    });
  }, [category, activeSub, sort, range]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setActiveSub("all");
    setSort("latest");
    setRange("all");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            该分类不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的分类可能已被移除或链接有误</p>
          <Link
            to="/"
            className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: category.name },
            ]}
          />
        </div>

        <section className="relative mt-5 w-full overflow-hidden">
          <div className="relative h-52 md:h-64 w-full">
            <img
              src={category.cover}
              alt={category.name}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-4 md:px-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-2xl`}></i>
                  </span>
                  <div>
                    <h1 className="font-heading text-2xl font-bold text-white md:text-3xl">
                      {category.name}
                    </h1>
                    <p className="text-xs text-white/70 md:text-sm">共 {results.length} 篇内容</p>
                  </div>
                </div>
                <p className="mt-3 max-w-2xl text-sm text-white/85 md:text-base">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 mt-6">
          <CategoryFilters
            subcategories={category.subcategories}
            activeSub={activeSub}
            onSubChange={setActiveSub}
            sort={sort}
            onSortChange={setSort}
            range={range}
            onRangeChange={setRange}
          />
        </div>

        <div className="w-full px-4 md:px-8 mt-6 pb-4">
          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {pageItems.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                    上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPage(index + 1)}
                      className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                        currentPage === index + 1
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    下一页
                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-inbox-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
                暂时没有符合条件的内容
              </h3>
              <p className="mt-2 text-sm text-foreground-600">
                试试调整筛选条件，或返回首页浏览其他内容
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}